import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { I18n, MessageDescriptor } from "@lingui/core";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import type { IntakeData, UploadedMedia } from "@/lib/types";
import type { CardDef } from "../types";
import type { ProcedureType } from "@/lib/specialtyTags";
import { CardActions } from "./CardPrimitives";
import { displaySpecialtyName } from "../specialtyDisplayName";
import { useIntakeMobileChromeRegistration } from "../IntakeMobileChromeContext";
import {
  formatOptionSelection,
  type IntakeOptionLocale,
} from "@/lib/intakeOptionMaps";
import { formatRegionCodeForLocale } from "@/lib/regionDisplay";
import { QUESTION_TREE } from "@/components/intake/dentist/DynamicQuestionTree";
import { buildCardFlow, DENTISTRY_PROCEDURE_TYPE_ORDER } from "../buildCardFlow";
import { patientIntakeApi } from "@/lib/patientIntakeApi";
import { ReviewDocumentsUploadsSection } from "./ReviewDocumentsUploadsSection";
import { formatDateToMMDDYYYY } from "@/lib/dateUtils";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Camera, CheckCircle, Clock, Eye, RotateCcw, X } from "lucide-react";

interface ReviewCardProps {
  card: CardDef;
  data: IntakeData;
  onSubmit: () => void;
  onEdit: (cardId: string) => void;
  onUpdate: (patch: Partial<IntakeData>) => void;
  isSubmitting: boolean;
  error?: string | null;
  onBack: () => void;
}

const ORTHODONTIC_PHOTO_LABELS: Record<string, ReturnType<typeof msg>> = {
  upper_occlusal: msg`Upper Occlusal`,
  frontal_retracted_apart: msg`Frontal Retracted Apart`,
  lower_occlusal: msg`Lower Occlusal`,
  right_buccal: msg`Right Buccal`,
  frontal_in_occlusion: msg`Frontal In Occlusion`,
  left_buccal: msg`Left Buccal`,
};

const PHOTO_LABELS: Record<string, ReturnType<typeof msg>> = {
  front: msg`Front Smile`,
  leftProfile: msg`Left Profile`,
  rightProfile: msg`Right Profile`,
  upperArch: msg`Upper Arch`,
  lowerArch: msg`Lower Arch`,
  biteFront: msg`Bite Closed`,
};

/**
 * Files from "Documents & uploads" are registered with these photoPosition values.
 * They already appear under Documents & Uploads on review — do not hydrate them into
 * dental `additionalPhotos` (that caused duplicates for dentistry).
 */
function isDocumentsUploadsPhotoPosition(position: string | undefined): boolean {
  if (!position) return false;
  const p = position.replace(/-/g, "_").toLowerCase();
  return (
    p === "photos" ||
    p === "labsimaging" ||
    p === "idpassport"
  );
}

function collectUploadedDocumentMediaIds(uploads: IntakeData["uploads"]): Set<string> {
  const ids = new Set<string>();
  const buckets = [
    uploads?.photos,
    uploads?.labsImaging,
    uploads?.idPassport,
  ];
  for (const arr of buckets) {
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      if (
        typeof item === "object" &&
        item !== null &&
        "isUploaded" in item &&
        (item as UploadedMedia).isUploaded === true &&
        typeof (item as UploadedMedia).id === "string"
      ) {
        ids.add((item as UploadedMedia).id);
      }
    }
  }
  return ids;
}

const TRAVEL_PLAN_LABELS: Record<string, ReturnType<typeof msg>> = {
  Yes: msg`Traveling alone`,
  No: msg`Traveling with someone`,
  "No, with partner": msg`Traveling with partner`,
  "No, with family": msg`Traveling with family`,
  "No, with friends": msg`Traveling with friends`,
  yes: msg`Traveling alone`,
  no: msg`Traveling with someone`,
  considering: msg`Travel plans still being decided`,
};

const DENTAL_PROCEDURE_LABELS: Record<string, ReturnType<typeof msg>> = {
  implants: msg`Dental Implants`,
  veneers: msg`Veneers`,
  crowns: msg`Crowns`,
  whitening: msg`Teeth Whitening`,
  orthodontics: msg`Orthodontics/Braces`,
  restoration: msg`General Restoration`,
  cosmetic: msg`Cosmetic Dentistry`,
  other: msg`Other`,
};

/** Row labels on review — diagnostics / imaging / optical / … (extend per specialty as needed). */
const REVIEW_DYNAMIC_FIELD_LABELS: Partial<Record<string, MessageDescriptor>> = {
  diagnosticsPurpose: msg`Purpose`,
  diagnosticsType: msg`Type`,
  diagnosticsArea: msg`Area`,
  diagnosticsTimeSensitivity: msg`Time sensitivity`,
  opticalPrimaryReason: msg`Primary Reason`,
  opticalVisionCorrection: msg`Vision Correction`,
  opticalLaterality: msg`Laterality`,
  opticalSymptoms: msg`Symptoms`,
  opticalProcedureInterest: msg`Procedure Interest`,
};

const getProcedureName = (p: ProcedureType | string): string => {
  if (typeof p === "string") return p;
  return p?.name || p?.slug || "";
};

const humanizeKey = (key: string): string =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const prettifyDynamicLabel = (rawKey: string, procedureSlug: string): string => {
  const base = humanizeKey(rawKey);
  const slug = procedureSlug.toLowerCase();
  const stripPrefixes = [
    slug.includes("hair") ? "Hair Transplant " : "",
    slug.includes("vascular") ? "Vascular " : "",
    slug.includes("optical") ? "Optical " : "",
    slug.includes("plastic") ? "Plastic " : "",
    slug.includes("diagnostic") ? "Diagnostics " : "",
    slug === "fertility" ? "Fertility " : "",
    slug.includes("stem") ? "Stem Cell " : "",
    slug.includes("bariatric") ? "Bariatric " : "",
    slug.includes("wellness") ? "Wellness " : "",
    slug === "orthopedic" || slug === "orthopedics" ? "Orthopedic " : "",
    slug === "neuro-surgery" ? "Neuro Surgery " : "",
    slug === "ent" || slug === "ear-nose-throat" || slug === "ear-nose-and-throat"
      ? "Ent "
      : "",
  ].filter(Boolean);

  for (const prefix of stripPrefixes) {
    if (base.startsWith(prefix)) return base.slice(prefix.length);
  }
  return base;
};

function extractQuestionLabel(
  q: { id: string; label: unknown },
  i18n: I18n,
): string {
  if (typeof q.label === "string") return q.label;
  if (q.label && typeof (q.label as { props?: { id?: string } }).props?.id === "string") {
    return i18n._((q.label as { props: { id: string } }).props.id) || humanizeKey(q.id);
  }
  return humanizeKey(q.id);
}

/**
 * Dynamic dental questions store select/multiselect values as Lingui message `id`
 * strings (same as `value` in IntakeV2Page card options). Raw IDs must be resolved
 * back to translated labels for review.
 */
function resolveDentalQuestionAnswer(
  q: { type?: string; options?: readonly unknown[] },
  raw: unknown,
  i18n: I18n,
): string {
  if (raw == null) return "";
  const qType = (q.type || "").toLowerCase();

  if (!q.options?.length || qType === "textarea" || qType === "text") {
    if (Array.isArray(raw)) {
      return raw
        .filter((x) => x !== "" && x != null)
        .map(String)
        .join(", ");
    }
    return String(raw).trim();
  }

  if (qType === "select" || qType === "multiselect") {
    const resolveOne = (v: string): string => {
      const trimmed = v.trim();
      if (!trimmed) return "";
      for (const opt of q.options!) {
        if (typeof opt === "string") {
          if (opt === trimmed) return opt;
          continue;
        }
        const descriptor = opt as MessageDescriptor;
        if (descriptor.id === trimmed) {
          return i18n._(descriptor);
        }
      }
      return trimmed;
    };

    if (Array.isArray(raw)) {
      return raw
        .filter((x) => x !== "" && x != null)
        .map((x) => resolveOne(String(x)))
        .filter(Boolean)
        .join(", ");
    }
    return resolveOne(String(raw));
  }

  if (Array.isArray(raw)) {
    return raw.filter((x) => x !== "" && x != null).join(", ");
  }
  return String(raw);
}

interface ReviewSection {
  title: string;
  cardId: string;
  rows: { label: string; value: string }[];
}

export function ReviewCard({
  card: _card,
  data,
  onSubmit,
  onEdit,
  onUpdate,
  isSubmitting,
  error,
  onBack,
}: ReviewCardProps) {
  const { i18n, _ } = useLingui();
  const [consentGiven, setConsentGiven] = useState(data.consentGiven);
  const [selectedPhoto, setSelectedPhoto] = useState<{
    id: string;
    title: string;
    image: string;
    mediaId?: string;
  } | null>(null);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

  const lastHydratedKeyRef = useRef<string | null>(null);
  const hasFetchedOnceRef = useRef(false);

  useEffect(() => {
    setConsentGiven(data.consentGiven);
  }, [data.consentGiven]);

  const procedureSlug =
    typeof data.procedures === "string"
      ? data.procedures
      : (data.procedures as ProcedureType)?.slug ?? "";
  const procedureName = displaySpecialtyName(
    procedureSlug,
    i18n,
    getProcedureName(data.procedures),
  );

  const optionLocale: IntakeOptionLocale =
    i18n.locale === "es" ? "es" : "en";
  const translatedQuestionLabelByFieldKey = useMemo(() => {
    const cards = buildCardFlow(data, "idle", [], i18n);
    const labelsByField = new Map<string, string>();
    cards.forEach((card) => {
      if (!card.fieldKey || !card.question) return;
      labelsByField.set(String(card.fieldKey), card.question);
    });
    return labelsByField;
  }, [data, i18n]);

  const isDentistry = procedureSlug === "dentistry";

  const getTravelPlanLabel = (value?: string) => {
    if (!value) return _(msg`Travel plans not specified`);
    const trimmed = value.trim();
    const label =
      TRAVEL_PLAN_LABELS[trimmed] ??
      TRAVEL_PLAN_LABELS[trimmed.toLowerCase()];
    return label ? _(label) : _(msg`Travel plans not specified`);
  };

  const handleConsentChange = (checked: boolean) => {
    const v = checked === true;
    setConsentGiven(v);
    onUpdate({ consentGiven: v });
  };

  const validPhotos = Object.entries(data.photos || {}).filter(
    ([, url]) => url && typeof url === "string" && url.length > 0,
  );
  const orthodonticPhotoCount = validPhotos.length;

  const openPhotoModal = useCallback(
    (photoId: string, title: string, image: string, mediaId?: string) => {
      setSelectedPhoto({ id: photoId, title, image, mediaId });
    },
    [],
  );

  const closePhotoModal = useCallback(() => {
    setSelectedPhoto(null);
  }, []);

  const handleRetakePhoto = useCallback(
    (photoId: string) => {
      const mediaId =
        selectedPhoto?.mediaId ??
        (data.photoMediaMap ? data.photoMediaMap[photoId] : undefined);
      closePhotoModal();
      onEdit("dental-photos");
      void mediaId;
    },
    [selectedPhoto, data.photoMediaMap, closePhotoModal, onEdit],
  );

  const localStateSnapshot = useMemo(() => {
    const photoValues = Object.values(data.photos || {});
    const additionalValues = data.additionalPhotos || [];
    const isLocalUrl = (url: unknown) =>
      typeof url === "string" &&
      (url.startsWith("data:") || url.startsWith("blob:"));

    const localPhotoCount = photoValues.filter(isLocalUrl).length;
    const localAdditionalCount = additionalValues.filter(isLocalUrl).length;

    return {
      localPhotoCount,
      localAdditionalCount,
    };
  }, [data.photos, data.additionalPhotos]);

  const hasLocalPrimaryPhotos = localStateSnapshot.localPhotoCount > 0;
  const hasLocalAdditionalPhotos = localStateSnapshot.localAdditionalCount > 0;

  const localHydrationKey = useMemo(
    () =>
      `${data.formUid ?? ""}:${localStateSnapshot.localPhotoCount}:${localStateSnapshot.localAdditionalCount}`,
    [
      data.formUid,
      localStateSnapshot.localPhotoCount,
      localStateSnapshot.localAdditionalCount,
    ],
  );

  const arraysShallowEqual = useCallback((a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }, []);

  useEffect(() => {
    if (!data.formUid) return;

    const shouldFetch =
      !hasFetchedOnceRef.current ||
      hasLocalPrimaryPhotos ||
      hasLocalAdditionalPhotos ||
      lastHydratedKeyRef.current !== localHydrationKey;

    if (!shouldFetch) return;

    lastHydratedKeyRef.current = localHydrationKey;

    let isCancelled = false;

    const hydratePhotosFromBackend = async () => {
      try {
        setIsLoadingPhotos(true);
        const response = await patientIntakeApi.getIntakeStatus(data.formUid!);
        const mediaItems: Array<{
          id?: string;
          downloadUrl?: string;
          photoPosition?: string;
          photoSequence?: number;
          createdAt?: string;
          [key: string]: unknown;
        }> = response.data?.media ?? [];

        if (
          isCancelled ||
          !Array.isArray(mediaItems) ||
          mediaItems.length === 0
        ) {
          return;
        }

        const sortedMedia = [...mediaItems].sort((a, b) => {
          const seqA =
            typeof a.photoSequence === "number"
              ? a.photoSequence
              : Number.MAX_SAFE_INTEGER;
          const seqB =
            typeof b.photoSequence === "number"
              ? b.photoSequence
              : Number.MAX_SAFE_INTEGER;
          if (seqA !== seqB) return seqA - seqB;
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateA - dateB;
        });

        const remotePhotos: Record<string, { url: string; mediaId: string }> =
          {};
        const remotePhotoMediaIds: Record<string, string> = {};
        const remoteAdditionalPhotos: { url: string; mediaId: string }[] = [];
        const seenAdditionalMedia = new Set<string>();
        const documentUploadMediaIds = collectUploadedDocumentMediaIds(
          data.uploads ?? {},
        );

        sortedMedia.forEach((item) => {
          const url =
            typeof item.downloadUrl === "string" ? item.downloadUrl : undefined;
          if (!url) return;

          const position = item.photoPosition as string | undefined;
          if (
            position &&
            (position in ORTHODONTIC_PHOTO_LABELS || position in PHOTO_LABELS)
          ) {
            remotePhotos[position] = {
              url,
              mediaId: item.id ?? url,
            };
            remotePhotoMediaIds[position] = item.id ?? url;
            return;
          }

          // General intake uploads (Documents & uploads) — not dental "additional"
          if (isDocumentsUploadsPhotoPosition(position)) {
            return;
          }
          const mediaId = typeof item.id === "string" ? item.id : "";
          if (mediaId && documentUploadMediaIds.has(mediaId)) {
            return;
          }

          if (item.id && !seenAdditionalMedia.has(item.id)) {
            remoteAdditionalPhotos.push({ url, mediaId: item.id });
            seenAdditionalMedia.add(item.id);
            return;
          }

          if (
            !item.id &&
            !remoteAdditionalPhotos.some((photo) => photo.url === url)
          ) {
            remoteAdditionalPhotos.push({ url, mediaId: url });
          }
        });

        const updates: Partial<IntakeData> = {};
        const existingPhotos = data.photos || {};

        const shouldUpdatePhotos = Object.entries(remotePhotos).some(
          ([key, value]) =>
            existingPhotos[key as keyof typeof existingPhotos] !== value.url,
        );

        if (shouldUpdatePhotos) {
          updates.photos = {
            ...existingPhotos,
            ...Object.fromEntries(
              Object.entries(remotePhotos).map(([key, value]) => [
                key,
                value.url,
              ]),
            ),
          };
        }

        if (Object.keys(remotePhotoMediaIds).length > 0) {
          updates.photoMediaMap = {
            ...(data.photoMediaMap ?? {}),
            ...remotePhotoMediaIds,
          };
        }

        if (remoteAdditionalPhotos.length > 0) {
          const newAdditional = remoteAdditionalPhotos.map((photo) => photo.url);
          const existingAdditional = data.additionalPhotos || [];

          if (!arraysShallowEqual(newAdditional, existingAdditional)) {
            updates.additionalPhotos = newAdditional;
            updates.additionalPhotoMediaIds = remoteAdditionalPhotos.map(
              (photo) => photo.mediaId,
            );
          } else if (
            !arraysShallowEqual(
              remoteAdditionalPhotos.map((photo) => photo.mediaId),
              data.additionalPhotoMediaIds ?? [],
            )
          ) {
            updates.additionalPhotoMediaIds = remoteAdditionalPhotos.map(
              (photo) => photo.mediaId,
            );
          }
        } else if ((data.additionalPhotos?.length ?? 0) > 0) {
          // Drop stale dental "additional" list when everything left is general uploads
          // (already shown under Documents & Uploads).
          updates.additionalPhotos = [];
          updates.additionalPhotoMediaIds = [];
        }

        if (!isCancelled && Object.keys(updates).length > 0) {
          onUpdate(updates);
        }
      } catch (error) {
        console.error("Failed to hydrate intake photos from backend:", error);
      } finally {
        if (!isCancelled) {
          setIsLoadingPhotos(false);
          hasFetchedOnceRef.current = true;
          lastHydratedKeyRef.current = localHydrationKey;
        }
      }
    };

    void hydratePhotosFromBackend();

    return () => {
      isCancelled = true;
    };
  }, [
    data.formUid,
    localHydrationKey,
    hasLocalPrimaryPhotos,
    hasLocalAdditionalPhotos,
    onUpdate,
    arraysShallowEqual,
    data.photos,
    data.additionalPhotos,
    data.photoMediaMap,
    data.uploads,
  ]);

  const personal: ReviewSection = {
    title: _(msg`Personal Information`),
    cardId: "firstName",
    rows: [
      { label: _(msg`First Name`), value: data.firstName },
      { label: _(msg`Last Name`), value: data.lastName },
      { label: _(msg`Email`), value: data.email },
      { label: _(msg`Phone`), value: data.phone },
      {
        label: _(msg`Country`),
        value: data.country
          ? formatRegionCodeForLocale(String(data.country), i18n.locale)
          : "",
      },
      {
        label: _(msg`Language`),
        value:
          data.preferredLanguage === "es"
            ? _(msg`Español`)
            : _(msg`English`),
      },
      ...(data.dateOfBirth?.trim()
        ? [
            {
              label: _(msg`Date of Birth`),
              value: formatDateToMMDDYYYY(data.dateOfBirth),
            },
          ]
        : []),
    ].filter((r) => r.value),
  };

  const procedureSection: ReviewSection = {
    title: _(msg`Speciality of Interest`),
    cardId: "procedures",
    rows: [{ label: _(msg`Speciality`), value: procedureName }].filter(
      (r) => r.value,
    ),
  };

  const supplementalHealthKeys: (keyof IntakeData)[] = [
    "preExistingConditions",
    "currentMedications",
    "allergies",
    "majorSurgeries",
    "smokingHabits",
    "alcoholUse",
    "height",
    "weight",
    "city",
    "state",
    "plannedTravelMonth",
    "travelingWith",
  ];

  const healthBackgroundRows: { label: string; value: string }[] = [];
  supplementalHealthKeys.forEach((key) => {
    const raw = data[key];
    if (raw === undefined || raw === null || raw === "") return;
    if (Array.isArray(raw) && raw.length === 0) return;
    const value = Array.isArray(raw) ? raw.join(", ") : String(raw);
    healthBackgroundRows.push({
      label: humanizeKey(String(key)),
      value,
    });
  });

  const healthBackgroundSection: ReviewSection | null =
    healthBackgroundRows.length > 0
      ? {
          title: _(msg`Health background`),
          cardId: "fullName",
          rows: healthBackgroundRows,
        }
      : null;

  const dynamicFields: [keyof IntakeData, string][] = [
    ["vascularPrimaryGoal", "vascularPrimaryGoal"],
    ["vascularSymptoms", "vascularSymptoms"],
    ["vascularSymptomTypes", "vascularSymptomTypes"],
    ["vascularDiagnosisStatus", "vascularDiagnosisStatus"],
    ["vascularUrgency", "vascularUrgency"],
    ["vascularProceduralExpectation", "vascularProceduralExpectation"],
    ["hairTransplantTreatmentAreas", "hairTransplantTreatmentAreas"],
    ["hairTransplantProgression", "hairTransplantProgression"],
    ["hairTransplantRelevantFactors", "hairTransplantRelevantFactors"],
    ["hairTransplantPreviousTreatments", "hairTransplantPreviousTreatments"],
    ["hairTransplantOutcomeExpectation", "hairTransplantOutcomeExpectation"],
    ["hairTransplantTimeline", "hairTransplantTimeline"],
    ["opticalPrimaryReason", "opticalPrimaryReason"],
    ["opticalVisionCorrection", "opticalVisionCorrection"],
    ["opticalLaterality", "opticalLaterality"],
    ["opticalSymptoms", "opticalSymptoms"],
    ["opticalProcedureInterest", "opticalProcedureInterest"],
    ["plasticAreaOfInterest", "plasticAreaOfInterest"],
    ["plasticProcedureType", "plasticProcedureType"],
    ["plasticSpecificConcern", "plasticSpecificConcern"],
    ["plasticPreviousSurgery", "plasticPreviousSurgery"],
    ["plasticOutcomePriority", "plasticOutcomePriority"],
    ["diagnosticsPurpose", "diagnosticsPurpose"],
    ["diagnosticsType", "diagnosticsType"],
    ["diagnosticsArea", "diagnosticsArea"],
    ["diagnosticsTimeSensitivity", "diagnosticsTimeSensitivity"],
    ["fertilityCareFocus", "fertilityCareFocus"],
    ["fertilityPatientType", "fertilityPatientType"],
    ["fertilityPreviousAttempts", "fertilityPreviousAttempts"],
    ["fertilityKnownDiagnosis", "fertilityKnownDiagnosis"],
    ["fertilityTimeline", "fertilityTimeline"],
    ["stemCellReason", "stemCellReason"],
    ["stemCellLocation", "stemCellLocation"],
    ["stemCellDiagnosisStatus", "stemCellDiagnosisStatus"],
    ["stemCellTreatmentGoal", "stemCellTreatmentGoal"],
    ["stemCellPriorTreatments", "stemCellPriorTreatments"],
    ["bariatricPrimaryGoal", "bariatricPrimaryGoal"],
    ["bariatricPriorWeightLoss", "bariatricPriorWeightLoss"],
    ["bariatricProcedureInterest", "bariatricProcedureInterest"],
    ["bariatricCurrentDiet", "bariatricCurrentDiet"],
    ["bariatricTimeline", "bariatricTimeline"],
    ["wellnessPrimaryGoal", "wellnessPrimaryGoal"],
    ["wellnessCurrentPractices", "wellnessCurrentPractices"],
    ["wellnessSpecificConcerns", "wellnessSpecificConcerns"],
    ["wellnessTimeline", "wellnessTimeline"],
    ["wellnessPreviousExperience", "wellnessPreviousExperience"],
    ["orthopedicPrimaryConcern", "orthopedicPrimaryConcern"],
    ["orthopedicAffectedArea", "orthopedicAffectedArea"],
    ["orthopedicDuration", "orthopedicDuration"],
    ["orthopedicPriorTreatments", "orthopedicPriorTreatments"],
    ["entPrimaryConcern", "entPrimaryConcern"],
    ["entSymptoms", "entSymptoms"],
    ["entDuration", "entDuration"],
    ["entPreviousSurgeries", "entPreviousSurgeries"],
    ["neuroSurgeryPrimaryConcern", "neuroSurgeryPrimaryConcern"],
    ["neuroSurgerySymptoms", "neuroSurgerySymptoms"],
    ["neuroSurgeryDiagnosisStatus", "neuroSurgeryDiagnosisStatus"],
    ["neuroSurgeryPriorTreatment", "neuroSurgeryPriorTreatment"],
    ["neuroSurgeryTimeline", "neuroSurgeryTimeline"],
    ["otherSpecialtyFocus", "otherSpecialtyFocus"],
    ["otherSpecialtyBodyAreas", "otherSpecialtyBodyAreas"],
    ["otherSpecialtySymptoms", "otherSpecialtySymptoms"],
    ["otherSpecialtyTimeline", "otherSpecialtyTimeline"],
    ["otherSpecialtyAdditionalInfo", "otherSpecialtyAdditionalInfo"],
  ];

  const procedureAnswerRows: { label: string; value: string }[] = [];
  dynamicFields.forEach(([key, mapKey]) => {
    const raw = data[key as keyof IntakeData];
    if (!raw || (Array.isArray(raw) && raw.length === 0)) return;
    let resolved = formatOptionSelection(
      mapKey,
      raw as string | string[],
      optionLocale,
    );
    if (key === "hairTransplantTreatmentAreas") {
      const note = data.hairTransplantTreatmentAreasOther?.trim();
      if (note) {
        resolved = resolved ? `${resolved} — ${note}` : note;
      }
    }
    if (resolved) {
      const keyStr = String(key);
      const mapped = REVIEW_DYNAMIC_FIELD_LABELS[keyStr];
      procedureAnswerRows.push({
        label:
          mapped
            ? _(mapped)
            : translatedQuestionLabelByFieldKey.get(keyStr) ??
              prettifyDynamicLabel(keyStr, procedureSlug),
        value: resolved,
      });
    }
  });

  const dynamicSection: ReviewSection | null =
    !isDentistry && procedureAnswerRows.length > 0 && procedureSlug
      ? {
          title: _(msg`Medical Questions`),
          cardId:
            procedureSlug.includes("vascular")
              ? "vascularPrimaryGoal"
              : procedureSlug.includes("hair")
                ? "hairTransplantTreatmentAreas"
                : procedureSlug.includes("optical")
                  ? "opticalPrimaryReason"
                  : procedureSlug.includes("plastic")
                    ? "plasticAreaOfInterest"
                    : procedureSlug.includes("diagnostic")
                      ? "diagnosticsPurpose"
                      : procedureSlug === "fertility"
                        ? "fertilityCareFocus"
                        : procedureSlug.includes("stem")
                          ? "stemCellReason"
                          : procedureSlug.includes("bariatric")
                            ? "bariatricPrimaryGoal"
                            : procedureSlug === "orthopedic" ||
                                procedureSlug === "orthopedics"
                              ? "orthopedicPrimaryConcern"
                              : procedureSlug === "ent" ||
                                  procedureSlug === "ear-nose-throat" ||
                                  procedureSlug === "ear-nose-and-throat"
                                ? "entPrimaryConcern"
                                : procedureSlug === "neuro-surgery"
                                  ? "neuroSurgeryPrimaryConcern"
                            : "wellnessPrimaryGoal",
          rows: procedureAnswerRows,
        }
      : null;

  const dentistryRows: { label: string; value: string }[] = [];
  if (isDentistry) {
    const rawDental = data.dentalProcedureType as unknown;
    const procedureTypes: string[] = Array.isArray(rawDental)
      ? (rawDental as string[])
      : typeof rawDental === "string" && rawDental.trim()
        ? [rawDental.trim()]
        : [];
    if (procedureTypes.length > 0) {
      const orderSet = new Set<string>(
        DENTISTRY_PROCEDURE_TYPE_ORDER as unknown as string[],
      );
      const ordered = DENTISTRY_PROCEDURE_TYPE_ORDER.filter((t) =>
        procedureTypes.includes(t),
      );
      const extras = procedureTypes.filter((t) => !orderSet.has(t));
      const typesOrdered = [...ordered, ...extras];
      const typeLabels = typesOrdered.map(
        (t) =>
          DENTAL_PROCEDURE_LABELS[t as keyof typeof DENTAL_PROCEDURE_LABELS] ??
          DENTAL_PROCEDURE_LABELS.other,
      );
      dentistryRows.push({
        label: _(msg`Dental procedure type`),
        value: typeLabels.map((lab) => _(lab)).join(", "),
      });
      for (const treeKey of typesOrdered) {
        const tree = QUESTION_TREE[treeKey as keyof typeof QUESTION_TREE];
        if (!tree?.questions) continue;
        for (const q of tree.questions) {
          const raw = (data as unknown as Record<string, unknown>)[q.id];
          const text = resolveDentalQuestionAnswer(q, raw, i18n);
          if (!text) continue;
          dentistryRows.push({
            label: extractQuestionLabel(q, i18n),
            value: text,
          });
        }
      }
    }
    if (data.dentalDiscomfort?.trim()) {
      dentistryRows.push({
        label: _(msg`Dental discomfort`),
        value: data.dentalDiscomfort,
      });
    }
    if (data.mobilityIssues?.trim()) {
      dentistryRows.push({
        label: _(msg`Mobility issues`),
        value: data.mobilityIssues,
      });
    }
    if (data.dietaryRestrictions?.trim()) {
      dentistryRows.push({
        label: _(msg`Dietary restrictions`),
        value: data.dietaryRestrictions,
      });
    }
  }

  const dentistrySection: ReviewSection | null =
    isDentistry && dentistryRows.length > 0
      ? {
          title: _(msg`Dental information`),
          cardId: "dentalProcedureType",
          rows: dentistryRows,
        }
      : null;

  const travelRows: { label: string; value: string }[] = [];
  if (data.budgetExpectations?.trim()) {
    travelRows.push({
      label: _(msg`Budget expectations`),
      value: data.budgetExpectations,
    });
  }
  if (data.travelAlone?.trim()) {
    travelRows.push({
      label: _(msg`Travel plans`),
      value: getTravelPlanLabel(data.travelAlone),
    });
  }
  if (data.travelConcerns?.trim()) {
    travelRows.push({
      label: _(msg`Travel concerns`),
      value: data.travelConcerns,
    });
  }
  if (data.personalInterest?.trim()) {
    travelRows.push({
      label: _(msg`Interest in Colombia`),
      value: data.personalInterest,
    });
  }

  const travelSection: ReviewSection | null =
    travelRows.length > 0
      ? {
          title: _(msg`Travel & budget`),
          cardId: "procedures",
          rows: travelRows,
        }
      : null;

  const labsUploadCount = (data.uploads?.labsImaging ?? []).length;
  const photosUploadCount = (data.uploads?.photos ?? []).length;
  const idUploadCount = (data.uploads?.idPassport ?? []).length;
  const docLinksCount = (data.documentReferenceLinks ?? []).filter(
    (l) => l?.url?.trim(),
  ).length;
  const hasUploadsPreview =
    labsUploadCount > 0 ||
    photosUploadCount > 0 ||
    idUploadCount > 0 ||
    docLinksCount > 0;

  const sections: ReviewSection[] = [
    personal,
    procedureSection,
    ...(healthBackgroundSection ? [healthBackgroundSection] : []),
    ...(dentistrySection ? [dentistrySection] : []),
    ...(dynamicSection ? [dynamicSection] : []),
    ...(travelSection ? [travelSection] : []),
  ];

  const handleSubmitClick = useCallback(() => {
    console.info("[Review Card] Submit clicked", {
      consentGiven,
      isDentistry,
      validPhotosCount: validPhotos.length,
    });

    if (!consentGiven) {
      console.warn("[Review Card] Blocked submit: consent not given");
      setTimeout(() => {
        const el = document.getElementById("v2-review-consent");
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => el?.focus?.(), 400);
      }, 50);
      return;
    }

    if (
      isDentistry &&
      validPhotos.length === 0 &&
      Object.keys(data.photos || {}).length > 0
    ) {
      console.warn("[Review Card] Blocked submit: invalid dentistry photos");
      alert(
        _(
          msg`Please retake your photos as they failed to upload properly. We need at least one valid photo to process your intake.`,
        ),
      );
      return;
    }

    onSubmit();
  }, [consentGiven, isDentistry, validPhotos.length, data.photos, onSubmit, _]);

  useIntakeMobileChromeRegistration(
    () => ({
      onContinue: handleSubmitClick,
      onCancel: onBack,
      continueLabel: _(msg`Submit`),
      continueLoading: isSubmitting,
      continueDisabled: !consentGiven,
    }),
    [handleSubmitClick, onBack, _, isSubmitting, consentGiven],
  );

  const photoModal =
    selectedPhoto &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4"
        onClick={closePhotoModal}
        role="presentation"
      >
        <div
          className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 p-4 sm:p-5">
            <h3 className="text-base font-semibold text-gray-900">
              {selectedPhoto.title}
            </h3>
            <div className="flex items-center gap-2">
              {!selectedPhoto.id.startsWith("additional_") && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  onClick={() => handleRetakePhoto(selectedPhoto.id)}
                >
                  <RotateCcw className="mr-1.5 h-4 w-4" />
                  <Trans>Retake</Trans>
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-xl"
                onClick={closePhotoModal}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
            <div
              className="flex items-center justify-center overflow-hidden rounded-xl bg-gray-100"
              style={{ maxHeight: "calc(90vh - 140px)" }}
            >
              <img
                src={selectedPhoto.image}
                alt=""
                className="max-h-full max-w-full object-contain"
              />
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <div className="w-full max-w-2xl">
      {photoModal}

      <div className="mb-8">
        <h2 className="text-[clamp(22px,5vw,34px)] font-bold leading-tight text-gray-900">
          <Trans>Review your information</Trans>
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          <Trans>
            Everything look right? You can use the back button if you need to
            make changes.
          </Trans>
        </p>
      </div>

      <div className="space-y-4">
        {sections.map((section) => (
          <div
            key={section.title}
            className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
          >
            <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-3 sm:px-5">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                {section.title}
              </h3>
            </div>

            <div className="divide-y divide-gray-50">
              {section.rows.map((row) => (
                <div
                  key={`${section.title}-${row.label}`}
                  className="flex flex-col items-start gap-1 px-4 py-3 sm:flex-row sm:gap-3 sm:px-5"
                >
                  <span className="pt-0.5 text-xs text-gray-400 sm:w-36 sm:flex-shrink-0">
                    {row.label}
                  </span>
                  <span className="flex-1 break-words text-sm font-medium text-gray-800">
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {hasUploadsPreview && (
          <ReviewDocumentsUploadsSection
            uploads={data.uploads}
            documentReferenceLinks={data.documentReferenceLinks}
            onEdit={() => onEdit("documents-and-links")}
          />
        )}

        {isDentistry && (
          <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-4 py-3 sm:px-5">
              <div className="flex items-center gap-2">
                <Camera className="h-4 w-4 text-[#166B93]" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  <Trans>Dental photos</Trans>
                </h3>
              </div>
              {orthodonticPhotoCount > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
                    <CheckCircle className="h-3.5 w-3.5" />
                    {orthodonticPhotoCount} {_(msg`photos`)}
                  </span>
                </div>
              )}
            </div>

            <div className="px-4 py-4 sm:px-5">
              {isLoadingPhotos && (
                <div className="mb-3 flex items-center gap-2 text-xs text-[#166B93]">
                  <Clock className="h-4 w-4" />
                  <Trans>Refreshing your photos…</Trans>
                </div>
              )}

              {orthodonticPhotoCount > 0 ? (
                <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                  {validPhotos.map(([key, url]) => {
                    const label =
                      ORTHODONTIC_PHOTO_LABELS[key] || PHOTO_LABELS[key];
                    const title = label
                      ? _(label)
                      : key
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase());

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() =>
                          openPhotoModal(
                            key,
                            title,
                            url as string,
                            data.photoMediaMap?.[key],
                          )
                        }
                        className="group !m-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 text-left transition-shadow hover:shadow-md sm:rounded-xl"
                      >
                        <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
                          <img
                            src={url as string}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                            <Eye className="h-4 w-4 text-white sm:h-5 sm:w-5" />
                          </div>
                        </div>
                        <p className="px-1 py-1.5 text-center text-[10px] font-medium leading-tight text-gray-700 sm:px-1.5 sm:text-[11px]">
                          {title}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl bg-gray-50 px-4 py-8 text-center">
                  <Camera className="mx-auto mb-2 h-10 w-10 text-gray-400" />
                  <p className="text-sm text-gray-600">
                    <Trans>No photos captured yet</Trans>
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    <Trans>Photos are used for your dental evaluation</Trans>
                  </p>
                </div>
              )}

              {data.additionalPhotos && data.additionalPhotos.length > 0 && (
                <div className="mt-4 rounded-xl border border-[#166B93]/15 bg-[#166B93]/5 p-3 sm:p-4">
                  <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-[#166B93]">
                    <Trans>
                      Additional photos ({data.additionalPhotos.length})
                    </Trans>
                  </h4>
                  <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                    {data.additionalPhotos.map((photo, index) => (
                      <button
                        key={`${index}-${photo.slice(0, 32)}`}
                        type="button"
                        onClick={() =>
                          openPhotoModal(
                            `additional_${index}`,
                            _(msg`Additional Photo ${index + 1}`),
                            photo,
                            data.additionalPhotoMediaIds?.[index],
                          )
                        }
                        className="group !m-0 overflow-hidden rounded-lg border border-white/80 bg-white text-left shadow-sm sm:rounded-xl"
                      >
                        <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
                          <img
                            src={photo}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/35 opacity-0 transition-opacity group-hover:opacity-100">
                            <Eye className="h-4 w-4 text-white sm:h-5 sm:w-5" />
                          </div>
                        </div>
                        <p className="px-1 py-1.5 text-center text-[10px] font-medium text-[#166B93] sm:text-[11px]">
                          <Trans>Problem area {index + 1}</Trans>
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        id="v2-review-consent"
        className="mt-6 rounded-xl border border-[#166B93]/15 bg-[#166B93]/5 p-4"
      >
        <div className="flex items-start gap-3">
          <Checkbox
            id="v2-review-consent-checkbox"
            checked={consentGiven}
            onCheckedChange={(c) => handleConsentChange(c === true)}
            className="mt-1"
          />
          <div className="flex-1">
            <label
              htmlFor="v2-review-consent-checkbox"
              className="cursor-pointer text-sm font-medium text-gray-900"
            >
              <Trans>
                I consent to securely sharing my photos and health information
                for evaluation.
              </Trans>
            </label>
            <p className="mt-2 text-xs leading-relaxed text-gray-600">
              <Trans>
                Your information is encrypted and used only for your
                consultation.
              </Trans>{" "}
              <a
                href="https://jemaservices.com.co/privacy-policy/"
                className="font-medium text-[#166B93] underline underline-offset-2"
                target="_blank"
                rel="noreferrer"
              >
                <Trans>Privacy Policy</Trans>
              </a>
            </p>
          </div>
        </div>
      </div>

      {!consentGiven && (
        <p className="mt-2 text-center text-xs text-gray-500">
          <Trans>Please confirm consent to submit</Trans>
        </p>
      )}

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="mt-8">
        <CardActions
          onNext={handleSubmitClick}
          onBack={onBack}
          nextLabel={_(msg`Submit`)}
          nextLoading={isSubmitting}
          nextDisabled={!consentGiven}
        />
      </div>
    </div>
  );
}
